// src/services/expenseService.js
import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';
import {
  createService,
  createNestedCollection
} from './factory';

// Helper function to download files
const downloadFile = (data, filename, mimeType = 'application/octet-stream') => {
  console.log('[downloadFile] Input data:', data);
  console.log('[downloadFile] Data type:', typeof data);
  console.log('[downloadFile] Is Blob:', data instanceof Blob);
  console.log('[downloadFile] Filename:', filename);
  console.log('[downloadFile] MIME type:', mimeType);
  
  // If data is already a Blob, use it directly
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  
  console.log('[downloadFile] Final blob size:', blob.size);
  console.log('[downloadFile] Final blob type:', blob.type);
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

// Create the base expense service using the factory
const expenseServiceBase = createService('Expense', '/finances/expenses', {
  nestedCollections: [
    createNestedCollection('receipt')
  ],
  customEndpoints: {
    // Submit expense for approval
    submitExpense: async (expenseId, approverId) => {
      return api.post(`/finances/expenses/${expenseId}/submit`, { approverId });
    },
    
    // Approve an expense
    approveExpense: async (expenseId, comment) => {
      return api.post(`/finances/expenses/${expenseId}/approve`, { comment });
    },
    
    // Reject an expense
    rejectExpense: async (expenseId, comment) => {
      return api.post(`/finances/expenses/${expenseId}/reject`, { comment });
    },
    
    // Mark expense as reimbursed
    reimburseExpense: async (expenseId, reimbursementData) => {
      return api.post(`/finances/expenses/${expenseId}/reimburse`, reimbursementData);
    },
    
    // Add expense to invoice
    addToInvoice: async (expenseId, invoiceId) => {
      return api.post(`/finances/expenses/${expenseId}/invoice`, { invoiceId });
    },
    
    // Get expense statistics
    getExpenseStats: async (params = {}) => {
      const queryString = objectToQueryString(params);
      return api.get(`/finances/expenses/stats${queryString}`);
    },
    
    // Upload receipt using pre-signed URL
    uploadReceipt: async (expenseId, file) => {
      try {
        console.log('[uploadReceipt] Starting upload for expense:', expenseId, 'file:', file.name);
        
        // Step 1: Get pre-signed URL
        const presignedResponse = await api.post(`/finances/expenses/${expenseId}/receipts`, {
          fileName: file.name,
          fileType: file.type
        });
        
        console.log('[uploadReceipt] Presigned response:', presignedResponse.data);
        
        // Handle both direct response and nested data structure
        const responseData = presignedResponse.data?.data || presignedResponse.data;
        const { url, fileUrl, key } = responseData;
        
        // Step 2: Upload file to S3 using pre-signed URL
        const uploadResponse = await fetch(url, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });
        
        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error('[uploadReceipt] S3 upload failed:', uploadResponse.status, errorText);
          throw new Error(`S3 upload failed: ${uploadResponse.status} ${errorText}`);
        }
        
        console.log('[uploadReceipt] File uploaded to S3 successfully');
        
        // Step 3: Notify server of successful upload
        const addReceiptResponse = await api.post(`/finances/expenses/${expenseId}/receipts`, {
          receipt: {
            key,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type
          }
        });
        
        console.log('[uploadReceipt] Receipt added to expense:', addReceiptResponse.data);
        
        return addReceiptResponse;
      } catch (error) {
        console.error('[uploadReceipt] Error uploading receipt:', error);
        throw error;
      }
    },
    
    // Delete receipt
    deleteReceipt: async (expenseId, receiptId) => {
      return api.delete(`/finances/expenses/${expenseId}/receipts/${receiptId}`);
    },
    
    // Get signed URL for a receipt
    getReceiptSignedUrl: async (receiptKey) => {
      try {
        console.log('[getReceiptSignedUrl] Getting URL for key:', receiptKey);
        const encodedKey = encodeURIComponent(receiptKey);
        const response = await api.get(`/files/${encodedKey}/url`);
        console.log('[getReceiptSignedUrl] Response:', response);
        
        // Handle different response structures
        const signedUrl = response.data?.signedUrl || response.signedUrl || response.data;
        console.log('[getReceiptSignedUrl] Signed URL:', signedUrl);
        
        return signedUrl;
      } catch (error) {
        console.error('[getReceiptSignedUrl] Error:', error);
        throw error;
      }
    }
  }
});

// Export expenses to Excel
export const exportExpenses = async (params = {}) => {
  try {
    // Clean up params before sending - handle special cases
    const cleanParams = { ...params };
    
    // Remove 'all' values
    Object.keys(cleanParams).forEach(key => {
      if (cleanParams[key] === 'all') {
        delete cleanParams[key];
      }
    });
    
    // Handle dateRange object if present
    if (cleanParams.dateRange && typeof cleanParams.dateRange === 'object') {
      if (cleanParams.dateRange.startDate) {
        cleanParams.startDate = cleanParams.dateRange.startDate;
      }
      if (cleanParams.dateRange.endDate) {
        cleanParams.endDate = cleanParams.dateRange.endDate;
      }
      delete cleanParams.dateRange;
    }
    
    const queryString = objectToQueryString(cleanParams);
    console.log('[expenseService] Exporting expenses with params:', cleanParams);
    
    const response = await api.get(`/finances/expenses/export${queryString}`, {
      responseType: 'blob'
    });
    
    console.log('[expenseService] Export response:', response);
    console.log('[expenseService] Response type:', typeof response);
    console.log('[expenseService] Is Blob:', response instanceof Blob);
    
    // Create filename with current date
    const filename = `expenses-${new Date().toISOString().split('T')[0]}.xlsx`;
    
    // Download the file with Excel MIME type
    downloadFile(response, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  } catch (error) {
    console.error('Error exporting expenses:', error);
    throw error;
  }
};

// Import expenses from Excel
export const importExpenses = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/finances/expenses/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data.result;
  } catch (error) {
    console.error('Error importing expenses:', error);
    throw error;
  }
};

// Override getAll to handle expense-specific params
const getExpenses = async (params = {}) => {
  try {
    const queryString = objectToQueryString(params);
    const response = await api.get(`/finances/expenses${queryString}`);
    // Return the full response to include status, meta, and data
    return response;
  } catch (error) {
    console.error('Error fetching expenses:', error);
    throw error;
  }
};

// Override create to handle expense creation
const createExpense = async (data) => {
  try {
    console.log('[expenseService] Creating expense with data:', data);
    console.log('[expenseService] Data type:', data instanceof FormData ? 'FormData' : 'JSON');
    
    if (data instanceof FormData) {
      console.log('[expenseService] FormData entries:');
      for (let [key, value] of data.entries()) {
        console.log(`  ${key}:`, value);
      }
    }
    
    // The create method will handle both JSON and FormData
    const response = await api.post('/finances/expenses', data);
    console.log('[expenseService] Response:', response);
    return response.data.expense || response.data.data?.expense;
  } catch (error) {
    console.error('Error creating expense:', error);
    throw error;
  }
};

// Export the enhanced expense service
const expenseService = {
  ...expenseServiceBase,
  getExpenses,
  createExpense,
  exportExpenses,
  importExpenses
};

// Add aliases for consistency
export { getExpenses, createExpense };
export const getExpenseById = expenseService.getById;
export const updateExpense = expenseService.update;
export const deleteExpense = expenseService.delete;
export const submitExpense = expenseService.submitExpense;
export const approveExpense = expenseService.approveExpense;
export const rejectExpense = expenseService.rejectExpense;
export const reimburseExpense = expenseService.reimburseExpense;
export const addToInvoice = expenseService.addToInvoice;
export const getExpenseStats = expenseService.getExpenseStats;
export const uploadReceipt = expenseService.uploadReceipt;
export const deleteReceipt = expenseService.deleteReceipt;
export const getReceiptSignedUrl = expenseService.getReceiptSignedUrl;

export default expenseService;